import { useState } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import Slash from "./Slash";
import ScrambleText from "./ScrambleText";
import { ParallaxShader, ParallaxSpan, PointerLayer } from "./Parallax";
import { buzz } from "../lib/haptics";

const fields = [
  { name: "name", label: "Name", type: "text", placeholder: "Musashi Miyamoto" },
  { name: "email", label: "Email", type: "email", placeholder: "you@domain.com" },
  { name: "password", label: "Create Password", type: "password", placeholder: "min. 6 characters" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", password: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error("Name, email and a password (min 6 characters) are required.");
      return;
    }
    setSubmitting(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/members/join`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "failed");
        }
        buzz([18, 40, 22]);
        toast.success("Request received. You'll gain access once approved.");
        setForm({ name: "", email: "", password: "", message: "" });
        setDone(true);
      })
      .catch((err) => {
        toast.error(typeof err.message === "string" && err.message !== "failed" ? err.message : "Something went wrong. Please try again.");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <section id="contact" data-testid="contact" className="relative overflow-hidden bg-[#050505] py-28 md:py-40">
      <Toaster theme="dark" position="top-center" richColors />
      <ParallaxShader variant="embers" intensity={1} strength={24} />
      <ParallaxSpan className="ambient-red" style={{ width: "40rem", height: "40rem", bottom: "-10rem", left: "-10rem" }} strength={48} />
      <ParallaxSpan className="section-kanji font-jp -top-10 right-4 text-[13rem] md:text-[22rem]" strength={90}>
        侍
      </ParallaxSpan>
      <div className="relative z-10 mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 md:grid-cols-2 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <ScrambleText
            as="span"
            text="04 — REQUEST TO JOIN"
            className="font-mono2 mb-4 block text-xs uppercase tracking-[0.4em] text-[#d91616]"
          />
          <PointerLayer strength={-16}>
            <h2 className="text-glow-hover font-display text-5xl font-black leading-[0.88] tracking-tighter md:text-7xl">
              JOIN THE
              <br />
              <span className="text-stroke">RONIN.</span>
            </h2>
          </PointerLayer>
          <p className="mt-8 max-w-sm font-body text-base leading-relaxed text-white/55">
            Membership unlocks the private Toji dojo — the shop, the app portal,
            and future member-only services. Access is granted by approval only.
          </p>

          <ul className="mt-8 space-y-3 font-mono2 text-sm text-white/55">
            <li className="flex items-center gap-3"><span className="text-[#d91616]">↳</span> The Shop</li>
            <li className="flex items-center gap-3"><span className="text-[#d91616]">↳</span> App Portal</li>
            <li className="flex items-center gap-3"><span className="text-[#d91616]">↳</span> Member Services</li>
          </ul>

          <div className="mt-10 flex items-center gap-2 font-mono2 text-xs text-white/50">
            <ShieldCheck size={15} className="text-[#d91616]" />
            Already approved?
            <Link
              to="/members"
              data-testid="member-login-link"
              className="text-[#d91616] underline-offset-4 transition-colors hover:text-white hover:underline"
            >
              Member login →
            </Link>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="glass rounded-2xl p-8 md:p-10"
        >
          {done ? (
            <div data-testid="join-success" className="flex min-h-[380px] flex-col items-center justify-center text-center">
              <span className="grid h-16 w-16 place-items-center rounded-full border border-[#d91616]/50 text-[#d91616]">
                <ShieldCheck size={28} />
              </span>
              <h3 className="font-display mt-6 text-2xl font-black tracking-tight text-white">Request received</h3>
              <p className="mt-3 max-w-xs font-body text-sm leading-relaxed text-white/55">
                Your application is under review. Once approved, sign in with your
                email and password to enter the members area.
              </p>
              <Link
                to="/members"
                data-testid="join-success-login"
                className="mt-8 font-mono2 text-xs uppercase tracking-[0.25em] text-[#d91616] underline-offset-4 hover:underline"
              >
                Go to member login →
              </Link>
            </div>
          ) : (
            <>
              <div className="space-y-6">
                {fields.map((f) => (
                  <div key={f.name}>
                    <label className="font-mono2 mb-2 block text-[10px] uppercase tracking-[0.2em] text-white/40">
                      {f.label}
                    </label>
                    <input
                      data-testid={`input-${f.name}`}
                      type={f.type}
                      value={form[f.name]}
                      onChange={(e) => update(f.name, e.target.value)}
                      placeholder={f.placeholder}
                      autoComplete={f.name === "password" ? "new-password" : f.name}
                      className="w-full border-b border-[#2a2a2a] bg-transparent py-3 font-body text-white outline-none transition-colors placeholder:text-white/25 focus:border-[#d91616]"
                    />
                  </div>
                ))}
                <div>
                  <label className="font-mono2 mb-2 block text-[10px] uppercase tracking-[0.2em] text-white/40">
                    Message <span className="text-white/25">(optional)</span>
                  </label>
                  <textarea
                    data-testid="input-message"
                    rows={3}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us why you'd like to join..."
                    className="w-full resize-none border-b border-[#2a2a2a] bg-transparent py-3 font-body text-white outline-none transition-colors placeholder:text-white/25 focus:border-[#d91616]"
                  />
                </div>
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={submitting}
                className="slash-host group mt-10 flex w-full items-center justify-center gap-2 bg-[#d91616] py-4 font-mono2 text-xs uppercase tracking-[0.3em] text-white transition-colors hover:bg-[#7a0000] disabled:opacity-60"
              >
                {submitting ? "Sending..." : "Request Access"}
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                <Slash />
              </button>
            </>
          )}
        </motion.form>
      </div>
    </section>
  );
}
